/**
 * API Service Module
 *
 * This module serves as the central hub for all HTTP communications with the backend.
 * It provides a standardized way to make authenticated API calls and handle responses.
 *
 * Key Features:
 * 1. Authentication Management:
 *    - Automatically handles token refresh
 *    - Manages session persistence
 *    - Handles authentication errors gracefully
 *
 * 2. Request/Response Interceptors:
 *    - Automatically adds authentication tokens to requests
 *    - Handles token expiration and refresh
 *    - Manages error responses consistently
 *
 * 3. CRUD Operations:
 *    - Provides standardized methods for Create, Read, Update, Delete operations
 *    - Handles different response formats
 *    - Manages error states uniformly
 *
 * 4. Error Handling:
 *    - Centralized error management
 *    - Consistent error response format
 *    - Automatic redirection for authentication failures
 */

import axios from "axios";
import Cookies from "js-cookie";
import { refreshSession } from "./authenticate";
import {
  redirectToLogin,
  redirectToUserHasNoAccess,
} from "../utils/rawFunction";

// Base URL for all API requests
const API_BASE_URL = "https://api.silanga.ca/v1";

/**
 * Retrieves the access token from cookies
 * @returns {string|null} The access token or null if not found
 */
const getAccessToken = () => {
  return Cookies.get("accessToken");
};

// Create axios instance with base URL
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Create unauthenticated axios instance for public endpoints for kiosk play mode
const publicApi = axios.create({
  baseURL: API_BASE_URL,
});

/**
 * Request Interceptor
 *
 * This interceptor runs before every API request to:
 * 1. Check for valid authentication token
 * 2. Automatically refresh token if expired
 * 3. Add authentication headers
 * 4. Handle token-related errors
 */
api.interceptors.request.use(
  async (config) => {
    const token = getAccessToken();
    if (!token) {
      try {
        const session = await refreshSession();
        token = session.getAccessToken().getJwtToken();
        Cookies.set("accessToken", token, {
          secure: true,
          sameSite: "Strict",
        });
      } catch (error) {
        // Handle refresh failure (e.g., redirect to login)
        redirectToLogin();
        return Promise.reject(error);
      }
    }

    if (token) {
      config.headers["token"] = `${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor
 *
 * This interceptor handles all API responses to:
 * 1. Detect authentication failures
 * 2. Automatically refresh tokens when needed
 * 3. Handle permission errors
 * 4. Manage error states consistently
 */
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (
      error.response.status === 403 &&
      error.response.data.message ===
        "User does not have required permissions(Error code: 403)"
    ) {
      redirectToUserHasNoAccess(error.response.data.message);
      return;
    }

    if (error.response.status === 403 && !originalRequest._retry) {
      // actually status code should be 401 but we use 403 for now as we have the custom authorization lambda which gives 403 instead of 401
      originalRequest._retry = true;
      try {
        console.log("--", error.response.data.message);
        const session = await refreshSession();
        const token = session.getAccessToken().getJwtToken();
        axios.defaults.headers.common["token"] = `${token}`;
        Cookies.set("accessToken", token, {
          secure: true,
          sameSite: "Strict",
        });
        return api(originalRequest);
      } catch (refreshError) {
        // Handle refresh failure (e.g., redirect to login)
        redirectToLogin();
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

/**
 * CRUD Operations
 *
 * These functions provide standardized methods for:
 * 1. Creating new resources (POST)
 * 2. Reading existing resources (GET)
 * 3. Updating resources (PATCH)
 * 4. Deleting resources (DELETE)
 *
 * Each function includes:
 * - Proper error handling
 * - Authentication management
 * - Consistent response formatting
 */

/**
 * Deletes data from the specified endpoint
 * @param {string} endpoint - The API endpoint
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} The response data
 * @throws {Error} If the request fails
 */
export const deleteFormData = async (endpoint, params) => {
  try {
    const response = await api.delete(endpoint, { params });
    return response.data;
  } catch (error) {
    if (error.response) {
      // The request was made and the server responded with a status code
      throw new Error(error.response.data.message || "An error occurred");
    } else if (error.request) {
      // The request was made but no response was received
      throw new Error("No response received from server");
    } else {
      // Something happened in setting up the request that triggered an Error
      throw new Error("Error setting up the request");
    }
  }
};

/**
 * Posts data to the specified endpoint
 * @param {string} endpoint - The API endpoint
 * @param {Object} data - The data to post
 * @returns {Promise<Object>} The response data
 * @throws {Error} If the request fails
 */
export const postFormData = async (endpoint, data) => {
  try {
    const response = await api.post(endpoint, data, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response.data;
  } catch (error) {
    console.log(error);

    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      throw new Error(
        error.response.data.message ||
          error.response.data.error ||
          "An error occurred"
      );
    } else if (error.request) {
      // The request was made but no response was received
      throw new Error("No response received from server");
    } else {
      // Something happened in setting up the request that triggered an Error
      throw new Error("Error setting up the request");
    }
  }
};

/**
 * Retrieves data from the specified endpoint
 * @param {string} endpoint - The API endpoint
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} The response data
 * @throws {Error} If the request fails
 */
export const getFormData = async (endpoint, params) => {
  try {
    const response = await api.get(endpoint, { params });
    return response.data;
  } catch (error) {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      throw new Error(error.response.data.message || "An error occurred");
    } else if (error.request) {
      // The request was made but no response was received
      throw new Error("No response received from server");
    } else {
      // Something happened in setting up the request that triggered an Error
      throw new Error("Error setting up the request");
    }
  }
};

/**
 * Updates metadata mapping for an AWS device
 * @param {string} id - The ID of the metadata mapping
 * @param {Object} data - The updated metadata
 * @returns {Promise<Object>} The response data
 * @throws {Error} If the update fails
 */
export const updateMetadataMapping = async (id, data) => {
  try {
    const response = await api.patch(`/aws-metadata-mapping`, data, {
      params: { id: id },
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.error || "Error updating Metadata mapping"
    );
  }
};

/**
 * Updates a flag's data
 * @param {string} id - The ID of the flag
 * @param {Object} data - The updated flag data
 * @returns {Promise<Object>} The response data
 * @throws {Error} If the update fails
 */
export const updateFlag = async (id, data) => {
  try {
    const response = await api.patch(`/flag`, data, {
      params: { id: id },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Error updating flag");
  }
};

/**
 * Deletes multiple flags by their IDs
 * @param {string[]} ids - Array of flag IDs to delete
 * @returns {Promise<Object>} Success message and count of deleted flags
 * @throws {Error} If any deletion fails
 */
export const deleteFlags = async (ids) => {
  const results = [];
  const failedIds = [];

  for (const id of ids) {
    try {
      const response = await api.delete("/flag", {
        params: { id: id },
      });
      results.push(response.data);
    } catch (error) {
      console.error(`Error deleting flag with id ${id}:`, error);
      failedIds.push(id);
    }
  }

  if (failedIds.length > 0) {
    throw new Error(`Failed to delete flags with ids: ${failedIds.join(", ")}`);
  }

  return { message: `Successfully deleted ${results.length} flags` };
};

/**
 * Updates an offset's data
 * @param {string} id - The ID of the offset
 * @param {Object} data - The updated offset data
 * @returns {Promise<Object>} The response data
 * @throws {Error} If the update fails
 */
export const updateOffset = async (id, data) => {
  try {
    const response = await api.patch(`/offset`, data, {
      params: { id: id },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Error updating offset");
  }
};

/**
 * Deletes multiple offsets by their IDs
 * @param {string[]} ids - Array of offset IDs to delete
 * @returns {Promise<Object>} Success message and count of deleted offsets
 * @throws {Error} If any deletion fails
 */
export const deleteOffset = async (ids) => {
  const results = [];
  const failedIds = [];

  for (const id of ids) {
    try {
      const response = await api.delete("/offset", {
        params: { id: id },
      });
      results.push(response.data);
    } catch (error) {
      console.error(`Error deleting offset with id ${id}:`, error);
      failedIds.push(id);
    }
  }

  if (failedIds.length > 0) {
    throw new Error(
      `Failed to delete offset with ids: ${failedIds.join(", ")}`
    );
  }

  return { message: `Successfully deleted ${results.length} offset` };
};

/**
 * PATCH helper for /kiosk config
 */
export const patchFormData = async (endpoint, data) => {
  try {
    const response = await api.patch(endpoint, data, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(
        error.response.data.message ||
          error.response.data.error ||
          "An error occurred"
      );
    } else if (error.request) {
      throw new Error("No response received from server");
    } else {
      throw new Error("Error setting up the request");
    }
  }
};

/**
 * Special GET for Kiosk config that attaches status code to errors
 */
export const getKioskFormData = async (endpoint, params) => {
  try {
    const response = await api.get(endpoint, { params });
    return response.data;
  } catch (error) {
    if (error.response) {
      const err = new Error(error.response.data.message || "An error occurred");
      err.status = error.response.status;
      err.response = error.response;
      throw err;
    } else if (error.request) {
      throw new Error("No response received from server");
    } else {
      throw new Error("Error setting up the request");
    }
  }
};

export const getKioskFormDataForPlayMode = async (endpoint, params) => {
  try {
    const response = await publicApi.get(endpoint, { params });
    return response.data;
  } catch (error) {
    if (error.response) {
      const err = new Error(error.response.data.message || "An error occurred");
      err.status = error.response.status;
      err.response = error.response;
      throw err;
    } else if (error.request) {
      throw new Error("No response received from server");
    } else {
      throw new Error("Error setting up the request");
    }
  }
};

/**
 * Kiosk Community Config API
 */
export const getKioskConfig = async (communityId) => {
  return getKioskFormData("/kiosk", { communityId });
};
export const getKioskConfigForPlayMode = async (communityId) => {
  return getKioskFormDataForPlayMode("/kiosk", { communityId });
};

export const postKioskConfig = async (data) => {
  return postFormData("/kiosk", data);
};

export const patchKioskConfig = async (data) => {
  return patchFormData("/kiosk", data);
};

/**
 * Kiosk Image Upload API
 */
export const uploadKioskImage = async (data) => {
  return postFormData("/kiosk", data);
};

export const updateKioskImage = async (data) => {
  return postFormData("/kiosk", data);
};
